import Downshift from 'downshift';
import { useRouter } from 'next/navigation';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { Input } from '@/components/ui/input';
import { ProductDto } from '@/core/application/dtos/ProductDto';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';
import { useSearchHistory } from '@/libs/hooks/search-history';
import { cn } from '@/libs/utils';
import { useProductStore } from '@/presentation/hooks/useStores';

type SearchBarProps = {
  className?: string;
};

export const SearchBar: React.FC<SearchBarProps> = observer(({ className }) => {
  const history = useSearchHistory();
  const router = useRouter();
  const productStore = useProductStore();
  const downshiftId = useId();
  const [hasMounted, setHasMounted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const latestTypedQueryRef = useRef('');
  const results = ProductDto.fromDomainList(productStore.searchSuggestionResults).slice(0, 3);
  const termSuggestions = productStore.searchSuggestionTerms.slice(0, 3);
  const recentTerms = history.terms.slice(0, 5);
  const hasRecentTerms = recentTerms.length > 0;
  const hasTermSuggestions = termSuggestions.length > 0;
  const hasProductResults = results.length > 0;
  const normalizedSearchTerm = searchTerm.trim();
  const hasMenuContent = hasProductResults || hasTermSuggestions || hasRecentTerms;

  const trackSearchSubmitted = (
    submittedQuery: string,
    submissionMethod: 'enter_key' | 'submit_button'
  ) => {
    trackConsumerEvent({
      eventType: 'search_submitted',
      source: 'marketplace.web',
      properties: {
        query: submittedQuery,
        results_count: results.length,
        submission_method: submissionMethod,
        surface: 'header_search',
      },
    });
  };

  const navigateToSearchResults = (
    submissionMethod: 'enter_key' | 'submit_button',
    nextTerm?: string
  ) => {
    const term = (nextTerm ?? searchTerm).trim();
    if (!term) {
      return;
    }

    setSearchTerm(term);
    latestTypedQueryRef.current = term;
    history.addTerm(term);
    productStore.clearSearchSuggestions();
    trackSearchSubmitted(term, submissionMethod);
    router.push(`/search?q=${encodeURIComponent(term)}`);
  };

  const navigateToRecentSearchTerm = (term: string) => {
    navigateToSearchResults('submit_button', term);
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) {
      return;
    }

    const term = searchTerm.trim();
    if (!term) {
      productStore.clearSearchSuggestions();
      return;
    }

    const handle = setTimeout(() => {
      void productStore.loadSearchSuggestions({ query: term, filters: { limit: 10 } });
    }, 250);

    return () => clearTimeout(handle);
  }, [hasMounted, productStore, searchTerm]);

  if (!hasMounted) {
    return (
      <div className={cn('relative', className)}>
        <div className="flex w-full flex-col gap-1">
          <div className="relative flex gap-0.5">
            <img
              src="/assets/svgs/Icons/MagnifyingGlass.svg"
              alt=""
              className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              placeholder="What are you looking to buy"
              className="h-[48px] w-full rounded-full py-4 pl-11 pr-4"
              readOnly
              tabIndex={-1}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Downshift
      id={downshiftId}
      inputValue={searchTerm}
      onStateChange={(changes) => {
        if (
          changes.type === Downshift.stateChangeTypes.changeInput &&
          typeof changes.inputValue === 'string'
        ) {
          latestTypedQueryRef.current = changes.inputValue.trim();
        }
      }}
      onChange={(selection) => {
        if (!selection) return;
        const resultRank = results.findIndex((item) => item.id === selection.id) + 1;
        const trackedQuery = String(
          latestTypedQueryRef.current || normalizedSearchTerm || selection.name || ''
        ).trim();
        trackConsumerEvent({
          eventType: 'search_suggestion_clicked',
          source: 'marketplace.web',
          properties: {
            query: trackedQuery,
            product_id: selection.id,
            rank: resultRank > 0 ? resultRank : 0,
            results_count: results.length,
            surface: 'header_search',
          },
        });
        productStore.clearSearchSuggestions();
        router.push(`/products/${selection.id}`);
      }}
      itemToString={(item) => (item ? item.name : '')}
      onInputValueChange={(value) => setSearchTerm(value || '')}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        selectedItem,
        isOpen,
      }) => (
        <div className={cn('relative', className)}>
          <div className="flex w-full flex-col gap-1">
            <form
              className="relative flex gap-0.5"
              onSubmit={(event) => {
                event.preventDefault();
                if (
                  highlightedIndex !== null &&
                  highlightedIndex >= 0 &&
                  highlightedIndex < results.length
                ) {
                  // Let Downshift own Enter-to-select when an item is highlighted.
                  return;
                }
                navigateToSearchResults('submit_button');
              }}
            >
              <img
                src="/assets/svgs/Icons/MagnifyingGlass.svg"
                alt=""
                className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                placeholder="What are you looking to buy"
                className={cn(
                  'h-[48px] w-full rounded-full py-4 pl-11',
                  normalizedSearchTerm ? 'pr-24' : 'pr-4'
                )}
                {...getInputProps({
                  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== 'Enter') {
                      return;
                    }
                    if (
                      highlightedIndex !== null &&
                      highlightedIndex >= 0 &&
                      highlightedIndex < results.length
                    ) {
                      return;
                    }
                    event.preventDefault();
                    navigateToSearchResults('enter_key');
                  },
                })}
              />
              {normalizedSearchTerm ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  className="absolute right-14 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-[#9CA3AF] transition-colors hover:text-[#6B7280]"
                  onClick={() => {
                    setSearchTerm('');
                    latestTypedQueryRef.current = '';
                    productStore.clearSearchSuggestions();
                  }}
                >
                  <span aria-hidden="true" className="text-2xl leading-none">
                    ×
                  </span>
                </button>
              ) : null}
              {normalizedSearchTerm ? (
                <button
                  type="button"
                  aria-label="Submit search"
                  className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#1A3FCC] text-white transition-colors hover:bg-[#1536B3]"
                  onClick={() => navigateToSearchResults('submit_button')}
                >
                  <span aria-hidden="true" className="text-lg leading-none">
                    &rarr;
                  </span>
                </button>
              ) : null}
            </form>
          </div>

          <ul
            className={`absolute top-12 z-10 mt-1 max-h-[400px] w-full overflow-y-auto rounded-2xl border border-[#E8E8E8] bg-white p-0 shadow-md ${
              !(isOpen && hasMenuContent) && 'hidden'
            }`}
            {...getMenuProps()}
          >
            {hasRecentTerms ? (
              <li className="list-none px-4 pb-3 pt-4">
                <p className="text-[13px] font-medium text-[#98A2B3]">Recent Search</p>
                <div className="scrollbar-hide mt-3 flex gap-2 overflow-x-auto pb-1">
                  {recentTerms.map((term, index) => (
                    <button
                      key={`${term}-${index}`}
                      type="button"
                      className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#F0F2F5] px-3 py-2 text-sm font-medium text-[#667085] transition-colors hover:bg-[#E8EBF0] hover:text-[#475467]"
                      onClick={() => navigateToRecentSearchTerm(term)}
                    >
                      <svg
                        className="size-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 7V12L15 14M22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="max-w-[120px] truncate">{term}</span>
                    </button>
                  ))}
                </div>
              </li>
            ) : null}
            {hasTermSuggestions ? (
              <li
                className={cn(
                  'list-none px-4 pb-2 pt-4',
                  hasRecentTerms && 'border-t border-[#EAECF0]'
                )}
              >
                <p className="mb-3 text-[13px] font-medium text-[#98A2B3]">Suggestions</p>
                <div className="space-y-1.5">
                  {termSuggestions.map((term, index) => (
                    <button
                      key={`${term}-${index}`}
                      type="button"
                      className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-[15px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                      onClick={() => navigateToSearchResults('submit_button', term)}
                    >
                      <img
                        src="/assets/svgs/Icons/MagnifyingGlass.svg"
                        alt=""
                        className="size-4 opacity-50"
                        aria-hidden="true"
                      />
                      <span className="truncate">{term}</span>
                    </button>
                  ))}
                </div>
              </li>
            ) : null}
            {hasProductResults ? (
              <li
                className={cn(
                  'list-none px-4 pb-2 pt-4',
                  (hasRecentTerms || hasTermSuggestions) && 'border-t border-[#EAECF0]'
                )}
              >
                <p className="mb-3 text-[13px] font-medium text-[#98A2B3]">Products</p>
              </li>
            ) : null}
            {isOpen
              ? results.map((item, index) => (
                <li
                  className={cn(
                    highlightedIndex === index && 'bg-[#F9FAFB]',
                    selectedItem === item && 'font-bold',
                    'mx-4 mb-1 flex cursor-pointer flex-col rounded-md px-2 py-2'
                  )}
                  key={item.id}
                  {...getItemProps({ item, index })}
                >
                  <div className="flex flex-row items-center gap-2">
                    <img
                      className="size-[48px] rounded-xl bg-[#EEF1F6] object-cover"
                      src={item.imageUrl || '/assets/svgs/Icons/Package.svg'}
                      alt={item.name}
                    />
                    <div>
                      <p className="line-clamp-2 text-[15px] font-semibold leading-6 text-[#1D2939]">
                        {item.name}
                      </p>
                      <p className="text-xs text-[#98A2B3]">{item.shopName}</p>
                    </div>
                  </div>
                </li>
              ))
              : null}
          </ul>
        </div>
      )}
    </Downshift>
  );
});
